import React, { ReactElement, useEffect } from 'react';
import { LiveComment } from '../../models/liveComment';
import { MessageType } from '../../types/messageType';
import './LiveMessageItem.scss';
import { getLevelColor } from '../../../../common/utils/utils';
import {
  LiveMatchKindType,
  LiveMatchType,
} from '../../../live-match/types/LiveMatchTypes';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../store/store';

export interface LiveMessageProps {
  liveComment: LiveComment;
  setShowScrollBottom: any;
  isAutoScroll: boolean;
  scrollListMessageToBottom: () => void;
}

export const LiveMessageItem = (props: LiveMessageProps): ReactElement => {
  const liveData = useSelector((state: AppState) => state.live.liveData);
  const {
    liveComment,
    isAutoScroll,
    setShowScrollBottom,
    scrollListMessageToBottom,
  } = props;

  const isSpecialLike =
    liveComment.commentConfig === MessageType.SEND_NICE &&
    liveComment.comment &&
    !liveComment.comment.startsWith('いいね');

  const getMessageTypeClass = (): string => {
    switch (liveComment.commentConfig) {
      case MessageType.SEND_MESSAGE:
      case MessageType.JOIN_USER:
      case MessageType.LEAVE_USER: {
        return 'live-message-item';
      }
      case MessageType.FOLLOW_USER:
      case MessageType.SEND_NICE: {
        return 'live-message-item--highlight';
      }
      case MessageType.SEND_GIFT: {
        return 'live-message-item--gift';
      }
      case MessageType.REQUEST_LIVE_MATCH:
      case MessageType.START_LIVE_MATCH: {
        switch (liveComment.commentUser?.kind) {
          case LiveMatchKindType.VS: {
            return 'live-message-item--live-vs';
          }
          case LiveMatchKindType.COLABO: {
            return 'live-message-item--live-collab';
          }
          default: {
            return 'live-message-item--highlight';
          }
        }
      }
      default: {
        return 'live-message-item';
      }
    }
  };

  const getMessageText = (): string | undefined => {
    switch (liveComment.commentConfig) {
      case MessageType.SEND_MESSAGE: {
        return liveComment.comment;
      }
      case MessageType.JOIN_USER: {
        return 'さんが入室しました';
      }
      case MessageType.LEAVE_USER: {
        return 'さんが退室しました';
      }
      case MessageType.FOLLOW_USER: {
        return 'さんがフォローしました';
      }
      case MessageType.SEND_NICE: {
        return liveComment.comment;
      }
      case MessageType.SEND_GIFT: {
        return `${liveComment.comment}を送信しました`;
      }
      case MessageType.REQUEST_LIVE_MATCH:
      case MessageType.START_LIVE_MATCH: {
        switch (liveComment.commentUser?.kind) {
          case LiveMatchKindType.VS: {
            if (liveComment.commentUser.type === LiveMatchType.SPECIFIED) {
              return 'さんからVS配信の指名が届きました！';
            } else {
              return 'さんとVSシャッフルでマッチングしました！';
            }
          }
          case LiveMatchKindType.COLABO: {
            if (liveComment.commentUser.type === LiveMatchType.SPECIFIED) {
              return 'さんからコラボ配信の指名が届きました！';
            } else {
              return 'さんとコラボシャッフルでマッチングしました！';
            }
          }
          default: {
            return undefined;
          }
        }
      }
      default: {
        return undefined;
      }
    }
  };

  useEffect(() => {
    if (isAutoScroll) {
      scrollListMessageToBottom();
      setShowScrollBottom(false);
      return;
    }
    setShowScrollBottom(true);
  }, []);

  return (
    <li className={getMessageTypeClass()}>
      {!isSpecialLike && (
        <>
          {/* user level */}
          <span
            className="live-message-item__level"
            style={{
              backgroundColor: getLevelColor(
                liveComment.commentUser?.live
                  ? liveComment.commentUser?.live?.user.level
                  : liveComment.commentUser?.level,
              ),
            }}
          >
            {liveComment.commentUser?.live
              ? liveComment.commentUser?.live?.user.level
              : liveComment.commentUser?.level}
          </span>

          {/* user name */}
          <span
            className="live-message-item__user"
            style={{
              color:
                liveComment.commentConfig === MessageType.SEND_GIFT ||
                liveComment.commentConfig === MessageType.REQUEST_LIVE_MATCH ||
                liveComment.commentConfig === MessageType.START_LIVE_MATCH
                  ? 'white'
                  : getLevelColor(
                      liveComment.commentUser?.live
                        ? liveComment.commentUser?.live?.user.level
                        : liveComment.commentUser?.level,
                    ),
            }}
          >
            {liveComment.commentUser?.live ? (
              <>
                {liveData?.id === liveComment.commentUser?.live?.id
                  ? liveComment.commentUser?.target?.user.loginId
                  : liveComment.commentUser?.live?.user.loginId}
              </>
            ) : (
              liveComment.commentUser?.loginId
            )}
          </span>
        </>
      )}

      {/* user message */}
      <span className="live-message-item__message">{getMessageText()}</span>
    </li>
  );
};
